import {
  Building2,
  CreditCard,
  Link as LinkIcon,
  Loader2,
  MoreVertical,
  Plus,
  Star,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  type SquareSavedCard,
  invalidateParentSquareCardQueries,
  useDisableParentSquareSavedCard,
  useGetParentSquareBillingGuard,
  useListParentSquareSavedCards,
  useSetParentSquareDefaultCard,
} from '~entities/payment/api/square-payment-methods';
import { PAYMENT_METHOD_REQUIRED_FOR_ACTIVE_ENROLLMENT } from '~entities/payment/api/v2-payment-card-paths';

import { SaveParentPaymentMethodDialog } from '~shared/components/save-parent-payment-method-dialog';
import {
  formatSavedPaymentMethodSubtitle,
  formatSavedPaymentMethodTitle,
  getSavedPaymentMethodIconKind,
} from '~shared/lib/format-saved-payment-method';
import { getErrorMessage } from '~shared/utils/error-handler';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~shared/shadcn/ui/alert-dialog';
import { Badge } from '~shared/shadcn/ui/badge';
import { Button } from '~shared/shadcn/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '~shared/shadcn/ui/dropdown-menu';

const LAST_CARD_REMOVAL_BLOCKED_TOAST =
  'Add a replacement payment method before removing your only one.';

const readPaymentErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object' || !('code' in error)) {
    return undefined;
  }
  return typeof error.code === 'string' ? error.code : undefined;
};

function PaymentMethodIcon({ type }: { type: string }) {
  const kind = getSavedPaymentMethodIconKind(type);
  if (kind === 'bank') {
    return <Building2 className='size-6 text-primary' />;
  }
  if (kind === 'link') {
    return <LinkIcon className='size-6 text-primary' />;
  }
  return <CreditCard className='size-6 text-primary' />;
}

export interface ParentSavedPaymentMethodsPanelProps {
  parentId: string;
  parentEmail?: string | null;
  /** When false, skip fetching (e.g. dialog closed). Defaults to true. */
  enabled?: boolean;
}

/**
 * Lists a parent's saved payment methods via V2 cards API (Stripe or Square).
 * Matches parent-portal saved-cards: list, set default, remove, add.
 */
export const ParentSavedPaymentMethodsPanel = ({
  parentId,
  parentEmail,
  enabled = true,
}: ParentSavedPaymentMethodsPanelProps) => {
  const savedCardsQuery = useListParentSquareSavedCards(parentId, enabled);
  const billingGuardQuery = useGetParentSquareBillingGuard(parentId, enabled);
  const setDefaultCard = useSetParentSquareDefaultCard();
  const disableCard = useDisableParentSquareSavedCard();
  const savedCards = savedCardsQuery.data ?? [];
  const canRemovePaymentMethod =
    billingGuardQuery.data?.canRemovePaymentMethod ?? true;

  const [addCardOpen, setAddCardOpen] = useState(false);
  const [cardToRemove, setCardToRemove] = useState<SquareSavedCard | null>(
    null,
  );

  const isBusy = setDefaultCard.isPending || disableCard.isPending;

  const handleSetDefaultCard = async (card: SquareSavedCard) => {
    try {
      await setDefaultCard.mutateAsync({ parentId, cardId: card.id });
      toast.success('Default payment method updated');
    } catch (error) {
      toast.error(
        getErrorMessage(error, 'Failed to set default payment method'),
      );
    }
  };

  const handleRemoveCard = async () => {
    if (!cardToRemove) {
      return;
    }
    try {
      await disableCard.mutateAsync({
        parentId,
        cardId: cardToRemove.id,
      });
      toast.success('Payment method removed');
      setCardToRemove(null);
    } catch (error) {
      toast.error(
        readPaymentErrorCode(error) ===
          PAYMENT_METHOD_REQUIRED_FOR_ACTIVE_ENROLLMENT
          ? getErrorMessage(error, LAST_CARD_REMOVAL_BLOCKED_TOAST)
          : getErrorMessage(error, 'Failed to remove payment method'),
      );
    }
  };

  if (savedCardsQuery.isLoading || billingGuardQuery.isLoading) {
    return (
      <div className='flex h-32 items-center justify-center'>
        <Loader2 className='size-8 animate-spin' />
      </div>
    );
  }

  if (savedCardsQuery.isError) {
    return (
      <p className='text-sm text-muted-foreground'>
        {getErrorMessage(
          savedCardsQuery.error,
          'Could not load saved cards for this parent',
        )}
      </p>
    );
  }

  return (
    <>
      {savedCards.length === 0 ? (
        <div className='space-y-4'>
          <p className='text-sm text-muted-foreground'>
            This parent has not saved any payment methods yet.
          </p>
          <Button
            className='w-full'
            onClick={() => {
              setAddCardOpen(true);
            }}
          >
            <Plus className='mr-2 size-4' />
            Add Payment Method
          </Button>
        </div>
      ) : (
        <div className='space-y-4'>
          {!canRemovePaymentMethod && (
            <p className='rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground'>
              This parent has an active enrollment, so this payment method must
              stay on file. Add an updated payment method first, then you can
              remove this one.
            </p>
          )}
          {savedCards.map((card) => {
            const canSetDefault = !card.isDefault;
            const showRemove = canRemovePaymentMethod;
            const subtitle = formatSavedPaymentMethodSubtitle(card);
            return (
              <div
                key={card.id}
                className='flex items-start gap-4 rounded-lg border p-4'
              >
                <div className='rounded-lg bg-primary/10 p-2'>
                  <PaymentMethodIcon type={card.type} />
                </div>
                <div className='grid flex-1 gap-1'>
                  <h3 className='font-semibold leading-none tracking-tight'>
                    {formatSavedPaymentMethodTitle(card)}
                  </h3>
                  {subtitle ? (
                    <p className='text-sm text-muted-foreground'>{subtitle}</p>
                  ) : null}
                  {card.isDefault ? (
                    <Badge variant='secondary' className='mt-1 w-fit'>
                      Default
                    </Badge>
                  ) : null}
                </div>
                {(canSetDefault || showRemove) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='ghost'
                        size='icon'
                        className='shrink-0'
                        disabled={isBusy}
                      >
                        <MoreVertical className='size-4' />
                        <span className='sr-only'>Payment method actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      {canSetDefault ? (
                        <DropdownMenuItem
                          onClick={() => {
                            void handleSetDefaultCard(card);
                          }}
                        >
                          <Star className='mr-2 size-4' />
                          Set as default
                        </DropdownMenuItem>
                      ) : null}
                      {showRemove ? (
                        <DropdownMenuItem
                          onClick={() => {
                            setCardToRemove(card);
                          }}
                          className='text-destructive focus:text-destructive'
                        >
                          <Trash2 className='mr-2 size-4' />
                          Remove
                        </DropdownMenuItem>
                      ) : null}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            );
          })}
          <Button
            className='w-full'
            variant={canRemovePaymentMethod ? 'secondary' : 'default'}
            onClick={() => {
              setAddCardOpen(true);
            }}
          >
            <Plus className='mr-2 size-4' />
            {canRemovePaymentMethod
              ? 'Add Payment Method'
              : 'Update payment method'}
          </Button>
        </div>
      )}

      <SaveParentPaymentMethodDialog
        open={addCardOpen}
        onOpenChange={setAddCardOpen}
        parentId={parentId}
        parentEmail={parentEmail}
        onSuccess={() => {
          invalidateParentSquareCardQueries(parentId);
        }}
      />

      <AlertDialog
        open={!!cardToRemove}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setCardToRemove(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Payment Method</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this payment method? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disableCard.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                void handleRemoveCard();
              }}
              disabled={disableCard.isPending}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {disableCard.isPending ? (
                <Loader2 className='mr-2 size-4 animate-spin' />
              ) : null}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
